import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useIntl } from 'umi';
import CardCommon from '@sg/components/CardCommon';
import { Justify, Table } from 'tea-component';
import RenderOperations from '@sg/components/Chart/Operations';
import Chart from '@sg/components/Chart';
import EventWindows from '@sg/components/EventWindows';
import EventSetting from '@sg/components/EventSetting';
import { pickOperations } from '@sg/commonData/pickOperations';
import TableContent from '@sg/components/TableContent';
import { scrollable } from 'tea-component/lib/table/addons';
import { getIEGGFinancialsDashboardData } from '@sg/services/index';
import DataProcessing from '@sg/components/DataProcessing';
import YoyMomDisplay from '@/pages/strategic/components/YoyMomDisplay';
import { xAxisHighLight } from '@sg/utils/chatUtils';
import './index.scss';
import { COEFFICIENT } from '../config';
import { dealTypeValue } from '@sg/utils/useChartOptions';
import MetricCard from '@/pages/strategic/pages/BusinessManagement/MetricCard';
import { rendTitle } from '@sg/utils/commonPageMethod';
import { toMillions, buildUpdateTime, mathFormat } from '@sg/utils/common';
import {
  IEGGFinancialsDashboardColumns,
  DefaultChat,
  RevenueNowProfitList,
  OverallNowProfitList,
} from './pageConfig';
import {
  useTrendCharts,
  SHOW_REVENUE_TREND_LIST,
} from './hooks/useTrendCharts';
import { useExeSummaryData } from './hooks/useExeSummaryData';

import {
  addYearSeparator,
  processIEGGFinancialsData,
  downloadInfoMap,
  getVisibleStackContext,
  buildAdaptiveCasualLabelData,
} from './utils';
import { messageTips } from '@gp/utils/common';
import { SGBuriedBtnPoint, createBtnPoint } from '@/pages/strategic/utils/buriedPoint/helper';
import { OverallFinancialsTab } from '@/pages/strategic/utils/buriedPoint/businessManagement';
import useDownload from '@/pages/strategic/hooks/useDownload';
import { useSize } from 'ahooks';
import EventToggleSetting from '@/pages/strategic/components/EventToggleSetting';
import useEventSetting from '@/pages/strategic/components/EventToggleSetting/useEventSetting';
import Loading from '@intelligence/components/Loading';
import { appendStackedTotalLabelSeries } from '@sg/utils/stackedTotalLabel';
import { isTooltipAllSeriesEmpty, withTooltipEmptyGuard } from '@sg/utils/tooltip';
import { useExpandCollapse } from '@sg/hooks/useExpandCollapse';
// import { collectCategoryIds } from '@/pages/strategic/utils/tableExpandUtils';

export default function OverallFinancials(props: any) {
  const { formatMessage } = useIntl();
  const { expandable, injectable, groupable } = Table.addons;
  const [expandedKeys, setExpandedKeys] = useState(['2', '3']);
  // 表格容器宽度监听
  // const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isNarrowWidth, setIsNarrowWidth] = useState(false);
  const { width } = useSize(document.body) || {};
  // Table data state
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableDataLoading, setTableDataLoading] = useState(false);
  const [revenueData, setRevenueData] = useState<any>([]);
  const [overallProfitData, setOverallProfitData] = useState<any>([]);
  const [incomePeriod, setIncomePeriod] = useState<string>('');
  const expandedKeysRef = useRef<string[]>(['2', '3']);
  // studioBreakdownData 已由 tableData 替代，用于 useExpandCollapse

  const [showChatsType, setShowChatsType] = useState<any>(DefaultChat);
  const [showOverallProfitChatsType, setShowOverallProfitChatsType] = useState<any>(DefaultChat);
  const chartRef = useRef<any>(null);
  const {
    chartEventSetting,
    setChartEventSetting,
    eventSettingVisible,
    setEventSettingVisible,
    isChartReady,
    setIsChartReady,
    eventDataByGame,
    showEventsDisabled,
  } = useEventSetting({
    revenueData,
    chartRef,
    showChatsTypeValue: showChatsType?.value,
    activeCard: 'Revenue',
    eventType: 'business',
  });

  const {
    allData,
    allOverallProfitData,
    revenueTrendLoading,
    profitTrendLoading,
    RevenueNowData,
    OverallNowProfitData,
    revenueUpdateTime,
    profitUpdateTime,
    fetchOverallGrossRevenueData,
    fetchOverallProfitData,
  } = useTrendCharts({
    formatMessage,
    revenueData,
    overallProfitData,
    setRevenueData,
    setOverallProfitData,
    showChatsType,
    showOverallProfitChatsType,
    chartEventSetting,
  });

  // Wrapper function for setShowChatsType with buried point tracking
  const handleShowChatsTypeChange = (e: any, downloadValue?: string) => {
    // Track buried point based on the selected value
    // For download, the second parameter is 'download' (only track when downloadValue is provided)
    // For bar/table, check e.value (but not when it's a download call)
    if (downloadValue === 'download') {
      // Only track download when the second parameter is explicitly 'download'
      // This prevents double tracking since RenderOperations calls change() twice for download
      const downloadType = e?.downloadType === 'chart' ? 'Download Chart' : 'Download Excel';
      const btnPoint = createBtnPoint(
        OverallFinancialsTab,
        'grossRevenueTrendDownload',
        `Gross Revenue Trend - ${downloadType}`,
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    } else if (e?.value && e.value !== 'download') {
      // Track bar/table only when e.value is set and it's not 'download'
      const value = e.value;
      const isViewClick = e.format !== showChatsType?.format && value === showChatsType?.value;

      if (value === 'view' || isViewClick) {
        const isViewOn =
          typeof e?.format === 'boolean' ? e.format : !showChatsType?.format;
        const btnPoint = createBtnPoint(
          OverallFinancialsTab,
          'grossRevenueTrendView',
          `Gross Revenue Trend - View ${isViewOn ? 'On' : 'Off'}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      } else if (value === 'bar') {
        // Only track if switching to bar (not already on bar)
        if (showChatsType?.value !== 'bar') {
          const btnPoint = createBtnPoint(
            OverallFinancialsTab,
            'grossRevenueTrendBarChart',
            'Gross Revenue Trend - Bar Chart',
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'table') {
        // Only track if switching to table (not already on table)
        if (showChatsType?.value !== 'table') {
          const btnPoint = createBtnPoint(
            OverallFinancialsTab,
            'grossRevenueTrendTable',
            'Gross Revenue Trend - Table',
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      }
    }

    // Call the original setter
    setShowChatsType(e);
  };

  // Resolve a row id to its display name (deeply through nested children) for expand/collapse buried-point extInfo.
  const findProjectNameById = useCallback((id: string): string => {
    const findInData = (items: any[]): string | null => {
      for (const item of items || []) {
        if (String(item?.id) === String(id)) {
          return item?.name || item?.project || item?.category || 'Unknown';
        }
        if (Array.isArray(item?.children) && item.children.length > 0) {
          const found = findInData(item.children);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    return findInData(tableData) || 'Unknown';
  }, [tableData]);

  // Wrapper function for setShowOverallProfitChatsType with buried point tracking
  const handleShowOverallProfitChatsTypeChange = (e: any, downloadValue?: string) => {
    // Track buried point based on the selected value
    // For download, the second parameter is 'download' (only track when downloadValue is provided)
    // For bar/table, check e.value (but not when it's a download call)
    if (downloadValue === 'download') {
      // Only track download when the second parameter is explicitly 'download'
      // This prevents double tracking since RenderOperations calls change() twice for download
      const downloadType = e?.downloadType === 'chart' ? 'Download Chart' : 'Download Excel';
      const btnPoint = createBtnPoint(
        OverallFinancialsTab,
        'profitTrendDownload',
        `Profit Trend - ${downloadType}`,
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    } else if (e?.value && e.value !== 'download') {
      // Track bar/table only when e.value is set and it's not 'download'
      const value = e.value;
      const isViewClick =
        e.format !== showOverallProfitChatsType?.format
        && value === showOverallProfitChatsType?.value;

      if (value === 'view' || isViewClick) {
        const isViewOn =
          typeof e?.format === 'boolean'
            ? e.format
            : !showOverallProfitChatsType?.format;
        const btnPoint = createBtnPoint(
          OverallFinancialsTab,
          'profitTrendView',
          `Profit Trend - View ${isViewOn ? 'On' : 'Off'}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      } else if (value === 'bar') {
        // Only track if switching to bar (not already on bar)
        if (showOverallProfitChatsType?.value !== 'bar') {
          const btnPoint = createBtnPoint(OverallFinancialsTab, 'profitTrendBarChart', 'Profit Trend - Bar Chart');
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'table') {
        // Only track if switching to table (not already on table)
        if (showOverallProfitChatsType?.value !== 'table') {
          const btnPoint = createBtnPoint(OverallFinancialsTab, 'profitTrendTable', 'Profit Trend - Table');
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      }
    }

    // Call the original setter
    setShowOverallProfitChatsType(e);
  };

  const { revenueConfig, profitConfig, fetchExecutiveSummaryData } =
    useExeSummaryData();
  // 递归处理子节点，支持无限层级展开
  const processChildrenRecursive = (items: any[], parentId: string, depth: number): any[] => {
    return items
      .filter((item: any) => item.category !== 'total')
      .map((item: any, index: number) => {
        const id = `${parentId}-${index + 1}`;
        const { category: rawCategory, children: rawChildren, ...rest } = item;
        const node: any = {
          id,
          category: rawCategory ? rawCategory.charAt(0).toUpperCase() + rawCategory.slice(1) : '',
          _depth: depth,
          ...rest,
        };
        if (Array.isArray(rawChildren) && rawChildren.length > 0) {
          node.children = processChildrenRecursive(rawChildren, id, depth + 1);
        }
        return node;
      });
  };

  // Process OverallIncome data for table display
  const processOverallIncomeData = (apiData: any) => {
    if (!apiData || !apiData.revenue || !apiData.profit) {
      return [];
    }

    const processedData: any[] = [];

    // Create Total Gross Revenue parent row
    const revenueTotal = apiData.revenue.find((item: any) => item.category === 'total');
    if (revenueTotal) {
      const { category: _, ...revenueTotalRest } = revenueTotal;
      processedData.push({
        id: '2',
        category: 'Total Gross Revenue',
        _depth: 0,
        ...revenueTotalRest,
        children: processChildrenRecursive(apiData.revenue, '2', 1),
      });
    }

    // Create Total Profit parent row
    const profitTotal = apiData.profit.find((item: any) => item.category === 'total');
    if (profitTotal) {
      const { category: __, ...profitTotalRest } = profitTotal;
      processedData.push({
        id: '3',
        category: 'Total Profit',
        _depth: 0,
        ...profitTotalRest,
        children: processChildrenRecursive(apiData.profit, '3', 1),
      });
    }

    return processedData;
  };

  // Fetch OverallIncome data
  const fetchOverallIncomeData = async () => {
    try {
      setTableDataLoading(true);
      const res: any = await getIEGGFinancialsDashboardData({});
      // console.log('Raw Overall Income API response:', res?.data);
      // Extract period from API response
      const endDate = res?.data?.datatime;
      if (endDate) {
        setIncomePeriod(endDate);
      }

      // const processedData = processOverallIncomeData(res?.data?.rows);
      const tableData = processIEGGFinancialsData(res?.data?.rows);
      // const tableData = processIEGGFinancialsData(oldDadas);
      setTableData(tableData);
      // setTableData(processedData);
      // 默认只展开第一级节点（depth=0），其他收起
      const topLevelKeys = tableData
        .filter((item: any) => item._depth === 0 && Array.isArray(item.children) && item.children.length > 0)
        .map((item: any) => String(item.id));
      setExpandedKeys(topLevelKeys);
    } catch (error) {
      messageTips('error', `Failed to fetch overall income data: ${error}`);
    } finally {
      setTableDataLoading(false);
    }
  };

  const { isDownloading, handleDownload } = useDownload(downloadInfoMap);

  const handleDownloadFun = async (
    downloadKey: keyof typeof downloadInfoMap,
  ) => {
    const downloadInfoObj = downloadInfoMap?.[downloadKey] || {};
    const btnPoint = createBtnPoint(
      OverallFinancialsTab,
      downloadInfoObj.downloadLogKey,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }

    await handleDownload(downloadKey as string);
  };

  // Custom download options for IEGG Financial Dashboard
  const ieggDownloadOptions = useMemo(() => [
    { label: "Download This Year's Data", onClick: () => handleDownloadFun('yearData') },
    { label: 'Download All Detailed Data', onClick: () => handleDownloadFun('detailedData') },
    { label: 'Download Time Series Data', onClick: () => handleDownloadFun('seriesData') },
  ], [handleDownloadFun]);

  const init = () => {
    fetchOverallGrossRevenueData();
    fetchOverallProfitData();
    fetchExecutiveSummaryData();
    fetchOverallIncomeData();
  };

  // 初始化
  useEffect(() => {
    init();
  }, []);

  // depth=0 的节点（如 IEGG Total）或 expanded: true 的节点固定展示，不参与一键展开/折叠
  const fixedExpandedKeys = useMemo(() => {
    const keys: string[] = [];
    const collectFixedKeys = (items: any[]) => {
      items.forEach((item: any) => {
        // depth=0 或 expanded: true 的节点固定展开
        if (
          (item._depth === 0 || item.expanded === true) &&
          Array.isArray(item.children) &&
          item.children.length > 0
        ) {
          keys.push(String(item.id));
        }
        if (Array.isArray(item.children) && item.children.length > 0) {
          collectFixedKeys(item.children);
        }
      });
    };
    collectFixedKeys(tableData);
    return keys;
  }, [tableData]);

  // 收集所有 depth > 0 且非 expanded: true 的可展开节点 key（始终递归所有 children）
  const toggleableKeys = useMemo(() => {
    const keys: string[] = [];
    const recurse = (items: any[]) => {
      items.forEach((item: any) => {
        // 排除 expanded: true 的节点，它们不参与一键展开/折叠
        if (
          item._depth > 0 &&
          item.expanded !== true &&
          Array.isArray(item.children) &&
          item.children.length > 0
        ) {
          keys.push(String(item.id));
        }
        if (Array.isArray(item.children) && item.children.length > 0) {
          recurse(item.children);
        }
      });
    };
    recurse(tableData);
    return keys;
  }, [tableData]);

  // Buried point: track row-level expand/collapse with row name in extInfo.
  const handleTrackRowToggle = useCallback((action: 'expand' | 'collapse', key: string) => {
    const projectName = findProjectNameById(key);
    const btnPoint = createBtnPoint(
      OverallFinancialsTab,
      'financialsProjectCollapse',
      `IEGG Financials Dashboard - Project ${action === 'expand' ? 'Expand' : 'Collapse'} - ${projectName}`,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
  }, [findProjectNameById]);

  // Buried point: track top expand-all / collapse-all action with one key.
  const handleTrackExpandAllToggle = useCallback((nextExpanded: boolean) => {
    const btnPoint = createBtnPoint(
      OverallFinancialsTab,
      'financialsProjectCollapse',
      `IEGG Financials Dashboard - Project ${nextExpanded ? 'Expand All' : 'Collapse All'}`,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
  }, []);

  const { isAllExpanded, handleExpandedKeysChange, stableToggleExpandAll } = useExpandCollapse({
    expandedKeys,
    setExpandedKeys,
    expandedKeysRef,
    data: tableData,
    fixedExpandedKeys,
    toggleableKeys,
    onTrackRowToggle: handleTrackRowToggle,
    onTrackExpandAllToggle: handleTrackExpandAllToggle,
  });

  // const topRef = useRef<HTMLDivElement>(null);
  // useEffect(() => {
  //   setTimeout(() => {
  //     topRef.current?.scrollIntoView({ behavior: 'smooth' });
  //   }, 400);
  // }, []);

  // 监听表格容器宽度，小于1500px时 isNarrowWidth 为 true
  useEffect(() => {
    if (width) {
      setIsNarrowWidth(width < 1685);
    } else {
      setIsNarrowWidth(false);
    }
  }, [width]);

  return (
    <>
      <div className="dbs-business-overall-financials">
        {/* <div ref={topRef} style={{ position: 'relative', top: '-300px' }}></div> */}
        <div className="dbs-home-studio-card">
          <MetricCard
            key={revenueConfig?.title}
            coefficient={COEFFICIENT}
            config={revenueConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
          />
          <MetricCard
            key={profitConfig?.title}
            coefficient={COEFFICIENT}
            config={profitConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
          />
        </div>
        <CardCommon
          title={rendTitle({
            title: `${formatMessage({ id: 'dbs_overall_gross_revenue_Trend' })} ($)`,
            tips: (
              <>
                <span>
                  {`${formatMessage({ id: 'dbs_update_on' })} ${revenueUpdateTime}`}
                </span>
                {(showChatsType?.value === 'bar') && (
                  <EventToggleSetting
                    showEventBubbles={chartEventSetting.showEventBubbles}
                    disabled={showEventsDisabled}
                    onToggle={(value) => {
                      const btnPoint = createBtnPoint(
                        OverallFinancialsTab,
                        'showEvents',
                        `Show Events - ${value ? 'On' : 'Off'}`,
                      );
                      if (btnPoint) {
                        SGBuriedBtnPoint(btnPoint);
                      }
                      setChartEventSetting({
                        ...chartEventSetting,
                        showEventBubbles: value,
                      });
                    }}
                    onOpenSettings={() => {
                      setEventSettingVisible(true);
                    }}
                  />
                )}
              </>
            ),
          })}
          operation={
            /* 本页下载有三个下载选项
              1、Download Thie Year's Data
              2、Download All Detailed Data
              3、Download Time Series Data
              分别使用后端接口以下1，2，3个接口，无需传参，且为post方法
               1、/api/v1/mgmt_pc/business/iegg_financial_dashboard/download
                2、/api/v1/mgmt_pc/business/iegg_financial_dashboard_all_detail/download
                3、/api/v1/mgmt_pc/business/iegg_financial_dashboard_time_series/download
                结合Strategic项目已有下载功能与组件，完成本页下载功能与下载组件的优化，允许从外部传下下载选项与下载接口调用
           */
            <RenderOperations
              defaultValue={showChatsType.value}
              defaultValueShow={showChatsType.format}
              change={handleShowChatsTypeChange}
              options={pickOperations(['view', ...SHOW_REVENUE_TREND_LIST, 'download'], formatMessage)}
              downloadParams={{
                ...allData.table,
                name: formatMessage({ id: 'dbs_overall_gross_revenue_Trend' }),
              }}
              enableDownloadDropdown={true}
            ></RenderOperations>
          }
        >
          <Justify
            bottom
            left={
              <div className="dbs-overall-financials-revenue-date">
                {RevenueNowProfitList(formatMessage).map((item: any) => {
                  const itemData = (RevenueNowData as any)?.[item.key];
                  const isYoyOrMom = item.key === 'yoy' || item.key === 'mom';

                  return (
                    <div className="dbs-overall-financials-item" key={item.key}>
                      <div className="dbs-overall-financials-item-header">
                        <div className="dbs-overall-financials-item-title">{item?.title}</div>
                      </div>
                      {isYoyOrMom ? (
                        <YoyMomDisplay date={itemData?.date} value={itemData?.value} type={item.key as 'yoy' | 'mom'} />
                      ) : (
                        <>
                          <div className="dbs-revenue-date-desc">{itemData?.date}</div>
                          <DataProcessing
                            content={['$', item?.content]}
                            data={itemData?.data}
                            rate={itemData?.rate || ''}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            }
            right={''}
          />
          {revenueTrendLoading ? (
            <Loading style={{ height: '443px' }} />
          ) : (
            <>
              {showChatsType?.value !== 'table' && allData[showChatsType?.value] && (
                <div style={{ position: 'relative' }}>
                  <Chart
                    key={`overall-revenue-${showChatsType?.value}-${chartEventSetting.showEventBubbles}`}
                    data={allData[showChatsType?.value]}
                    type={showChatsType?.value}
                    height="443px"
                    setOtherConfigFunc={(opt: any) => {
                      if (
                        chartEventSetting.showEventBubbles
                        && Object.keys(chartEventSetting.bubbleEvent || {}).length > 0
                      ) {
                        opt.grid.top = 140;
                      }
                      // opt.grid.containLabel = true;
                      // // opt.legend.type="plain"
                      opt = xAxisHighLight(opt, {
                        type: showChatsType?.value === 'bar' ? 'shadow' : 'line',
                        lineStyle: {
                          color: '#BBBDCB',
                          width: 1,
                          type: 'solid',
                        },
                      } as any);

                      // Add vertical line to separate 2024 and 2025
                      opt = addYearSeparator(opt);

                      // Legend display order only: Studio -> Publishing -> Casual -> KPI.
                      // Note: this does NOT control stacked bar draw order (that is set in series build logic).
                      if (showChatsType?.value === 'bar' && opt.series) {
                        const isHelperSeriesName = (name: string) => {
                          return [
                            '__total_label__',
                            'TopTipsEvent',
                            'Total',
                          ].includes(name) || name.includes('__year_separator_line');
                        };
                        const legendOrder = ['studio', 'publishing', 'casual', 'kpi'];
                        const legendNames: string[] = Array.from(
                          new Set<string>(
                            opt.series
                              .map((series: any) => series?.name)
                              .filter(
                                (name: string) =>
                                  name
                                  && !isHelperSeriesName(name),
                              ),
                          ),
                        );
                        const orderedLegendNames = [...legendNames].sort((left: string, right: string) => {
                          const leftOrder = legendOrder.findIndex(key => left.toLowerCase().includes(key));
                          const rightOrder = legendOrder.findIndex(key => right.toLowerCase().includes(key));
                          const normalizedLeftOrder = leftOrder === -1 ? Number.MAX_SAFE_INTEGER : leftOrder;
                          const normalizedRightOrder = rightOrder === -1 ? Number.MAX_SAFE_INTEGER : rightOrder;
                          if (normalizedLeftOrder !== normalizedRightOrder) {
                            return normalizedLeftOrder - normalizedRightOrder;
                          }
                          return legendNames.indexOf(left) - legendNames.indexOf(right);
                        });

                        if (Array.isArray(opt.legend)) {
                          opt.legend = opt.legend.map((legend: any) => ({
                            ...legend,
                            data: orderedLegendNames,
                          }));
                        } else if (opt.legend) {
                          opt.legend = {
                            ...opt.legend,
                            data: orderedLegendNames,
                          };
                        }
                      }

                      if (opt.tooltip && opt.tooltip.formatter) {
                        const originalFormatter = opt.tooltip.formatter;

                        // 辅助函数：处理 render 结果的宽度对齐
                        const processRenderResult = (html: string, nameWidth: number, valueWidth: number) => {
                          const columnStyle = (width: number, align: 'left' | 'right') =>
                            `width: ${width}px; text-align: ${align}; flex-shrink: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;

                          return html
                            .replace(
                              /class="dbs-base-chart-tooltip-item-name"/g,
                              `class="dbs-base-chart-tooltip-item-name" style="${columnStyle(nameWidth, 'left')}"`,
                            )
                            .replace(
                              /class="dbs-base-chart-tooltip-item-value"/g,
                              `class="dbs-base-chart-tooltip-item-value" style="${columnStyle(valueWidth, 'right')}"`,
                            )
                            .replace(/class="dbs-base-chart-tooltip-item"([^>]*)style="([^"]*)"/g, (match, attrs, styles) =>
                              styles.includes('width: 100%')
                                ? match
                                : `class="dbs-base-chart-tooltip-item"${attrs}style="${styles}; width: 100%;"`,
                            );
                        };

                        // 辅助函数：格式化值（复用 useChartOptions 的逻辑）
                        const formatValue = (itemData: any, value: any) => {
                          if (value === null || value === undefined) return 'NA';
                          return itemData.valueType ? dealTypeValue(itemData.valueType, value) : mathFormat(value);
                        };

                        const revenueTooltipFormatter = (params: any[]) => {
                          // Filter out the total label series (__total_label__) from tooltip
                          const isHelperSeriesName = (name: string) => {
                            return [
                              '__total_label__',
                              'TopTipsEvent',
                              'Total',
                            ].includes(name) || name.includes('__year_separator_line');
                          };
                          const filteredParams = Array.isArray(params)
                            ? params.filter((p: any) => !isHelperSeriesName(String(p.seriesName || '')))
                            : params;
                          // Tooltip row order only: keep Actual first (from studio renderer),
                          // then Studio -> Publishing -> Casual -> KPI.
                          const tooltipOrder = ['studio', 'publishing', 'casual', 'kpi'];
                          const orderedParams = Array.isArray(filteredParams)
                            ? [...filteredParams].sort((left: any, right: any) => {
                              const leftName = String(left?.seriesName || '').toLowerCase();
                              const rightName = String(right?.seriesName || '').toLowerCase();
                              const leftOrder = tooltipOrder.findIndex(key => leftName.includes(key));
                              const rightOrder = tooltipOrder.findIndex(key => rightName.includes(key));
                              const normalizedLeftOrder = leftOrder === -1 ? Number.MAX_SAFE_INTEGER : leftOrder;
                              const normalizedRightOrder = rightOrder === -1 ? Number.MAX_SAFE_INTEGER : rightOrder;
                              if (normalizedLeftOrder !== normalizedRightOrder) {
                                return normalizedLeftOrder - normalizedRightOrder;
                              }
                              return 0;
                            })
                            : filteredParams;

                          if (isTooltipAllSeriesEmpty(orderedParams, item => item?.value)) return '';

                          // 计算列宽
                          const tooltipWidth =
                            orderedParams[0]?.data?.tooltipWidth || (orderedParams[0]?.data?.names ? '210px' : '170px');
                          const namesArray = orderedParams[0]?.data?.names?.split(',') || [];
                          // Widened to fit "Casual (Non-MC)" without truncation.
                          const nameColumnWidth = 130;
                          const valueColumnWidth =
                            namesArray.length > 0
                              ? Math.floor((parseInt(tooltipWidth.replace('px', '')) - nameColumnWidth) / namesArray.length)
                              : 45;

                          const columnStyle = (width: number, align: 'left' | 'right') =>
                            `width: ${width}px; text-align: ${align}; flex-shrink: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;

                          // 表头行
                          const headerRow = `<div style="display:flex; width: 100%; color: #D8DAE4">
                      <div style="${columnStyle(nameColumnWidth, 'left')}">${orderedParams[0]?.name || ''}</div>
                      ${namesArray
                              .map(
                                (name: string) =>
                                  `<div style="${columnStyle(valueColumnWidth, 'right')}">${name || ''}</div>`,
                              )
                              .join('')}
                    </div>`;

                          // 数据行
                          const dataRows = orderedParams
                            .map((item: any) => {
                              const itemData = item?.data || {};

                              if (itemData.render) {
                                const renderResult = itemData.render(item);
                                return typeof renderResult === 'string'
                                  ? processRenderResult(renderResult, nameColumnWidth, valueColumnWidth)
                                  : renderResult;
                              }

                              const formattedValue = formatValue(itemData, itemData.tooltipValue ?? item.value);
                              const yoyValue = itemData.yoy?.total || itemData.yoy || '';
                              const momValue = itemData.mom?.total || itemData.mom || '';

                              return `${itemData.headSplit
                                ? `<div style="height: 1px; width: 100%; background-color: rgba(255, 255, 255, 0.4); margin: 5px 0 0 0;"></div>`
                                : ''
                                }
                        <div class="dbs-base-chart-tooltip-item" style="display: flex; width: 100%; margin-top: 10px;">
                          <span class="dbs-base-chart-tooltip-item-name" style="${columnStyle(
                                  nameColumnWidth,
                                  'left',
                                )}">
                            ${item.marker}<span>${item.seriesName}</span>
                          </span>
                          <span class="dbs-base-chart-tooltip-item-value" style="${columnStyle(
                                  valueColumnWidth,
                                  'right',
                                )}">
                            <span style="font-size: 14px; font-weight: 600; color: ${itemData.textColor || ''
                                }">${formattedValue}</span>
                            ${itemData.tip || ''}
                          </span>
                          ${namesArray
                                  .slice(1)
                                  .map((_item: string, idx: number) => {
                                    const value = idx === 0 ? yoyValue : momValue;
                                    return `<span class="dbs-base-chart-tooltip-item-value" style="${columnStyle(
                                      valueColumnWidth,
                                      'right',
                                    )}">
                              <span style="font-size: 14px; font-weight: 600">${value || ''}</span>
                            </span>`;
                                  })
                                  .join('')}
                        </div>`;
                            })
                            .join('');

                          return `<div style="width: ${tooltipWidth}; font-size: 12px;">${headerRow}${dataRows}</div>`;
                        };
                        opt.tooltip.formatter = withTooltipEmptyGuard(revenueTooltipFormatter);
                      }

                      // Configure data labels based on toggle state (format = true means show labels)
                      // This is the Gross Revenue Trend chart (RevenueTrendList) - stacked bar with studio/publishing
                      if (showChatsType?.format && opt.series && showChatsType?.value === 'bar') {
                        // Build legend-aware stack context once, then reuse for
                        // casual label placement and total-label enable logic.
                        const {
                          maxVisibleStackTotal,
                          visibleStackedMetricSeriesCount,
                        } = getVisibleStackContext(opt, ['studio', 'publishing', 'casual']);

                        // Studio and Publishing both show values at insideBottom
                        // Total (Actual = studio + publishing) shows on top of publishing series
                        opt.series = opt.series.map((series: any) => {
                          const seriesName = series.name?.toLowerCase();

                          // Ignore KPI (line chart) - don't add labels
                          if (seriesName?.includes('kpi') || series.type === 'line') {
                            return series;
                          }

                          if (seriesName?.includes('studio')) {
                            // Studio segment - show studio value at insideBottom
                            return {
                              ...series,
                              label: {
                                show: true,
                                position: 'inside',
                                fontSize: 12,
                                fontWeight: 'normal',
                                color: '#333',
                                formatter: (params: any) => {
                                  const value = params.data?.studio ?? params.value;
                                  if (value === null || value === undefined || Number(value) === 0) return '';
                                  return toMillions(value);
                                },
                              },
                            };
                          } else if (seriesName?.includes('publishing')) {
                            // Publishing segment - show publishing value at insideBottom
                            return {
                              ...series,
                              label: {
                                show: true,
                                position: 'inside',
                                fontSize: 12,
                                fontWeight: 'normal',
                                color: '#333',
                                formatter: (params: any) => {
                                  const value = params.data?.publishing ?? params.value;
                                  if (value === null || value === undefined || Number(value) === 0) return '';
                                  return toMillions(value);
                                },
                              },
                            };
                          } else if (seriesName?.includes('casual')) {
                            // Casual segment uses adaptive label placement.
                            // Goal:
                            // - keep normal cases inside;
                            // - avoid clipped text for tiny bars;
                            // - allow legend toggles to reflow label position automatically.
                            const adaptiveCasualData = buildAdaptiveCasualLabelData(series.data, {
                              maxVisibleStackTotal,
                              labelFormatter: (value: number) => toMillions(value),
                              ratioThreshold: 0.08,
                            });
                            return {
                              ...series,
                              data: adaptiveCasualData,
                              // Series-level label stays as default fallback.
                              // Per-point label in `adaptiveCasualData` overrides this when needed.
                              label: {
                                show: true,
                                position: 'inside',
                                fontSize: 12,
                                fontWeight: 'normal',
                                color: '#333',
                                formatter: (params: any) => {
                                  const value = params.data?.casual ?? params.value;
                                  if (value === null || value === undefined) return '';
                                  return toMillions(value);
                                },
                              },
                            };
                          } else {
                            // Other series - no labels
                            return series;
                          }
                        });

                        // Use shared stacked total utility so total automatically follows
                        // all visible stacked metrics (e.g. studio + publishing + casual).
                        // Count visible stacked metrics to avoid duplicate-label effect.
                        // If only one stack metric is visible (e.g. Casual only), showing both
                        // segment label + total label produces a shadow-like double text.
                        opt = appendStackedTotalLabelSeries(opt, {
                          // With only one visible stack metric (e.g. Casual only),
                          // the total label duplicates the same value and looks like a shadow.
                          enabled:
                            showChatsType?.value === 'bar'
                            && showChatsType?.format
                            && visibleStackedMetricSeriesCount > 1,
                          seriesName: '__total_label__',
                          labelFormatter: (value: number) => toMillions(value),
                        });
                      }

                      // opt.legend.formatter = (value: any) => {
                      //   const t: any = allData[showChatsType?.value]?.seriesData?.find((item: any) => item?.name === value)
                      //   return t?.text;
                      // };
                      return opt;
                    }}
                    onChartReady={(chartInstance: any) => {
                      chartRef.current = chartInstance;

                      const checkChartReady = () => {
                        const chartOption = chartInstance?.getOption();
                        const hasTopTipsEvent = chartOption?.series?.some((s: any) => s.name === 'TopTipsEvent');

                        if (hasTopTipsEvent) {
                          const xAxisData = chartOption?.xAxis?.[0]?.data || [];
                          const testXAxis = xAxisData.find((x: string) => x && x.length > 0);

                          if (testXAxis) {
                            try {
                              const testPosition = chartInstance.convertToPixel(
                                { seriesName: 'TopTipsEvent' },
                                [testXAxis, 0],
                              );
                              if (!isNaN(testPosition[0])) {
                                setIsChartReady(true);
                                return;
                              }
                            } catch (e) {
                              // convertToPixel not ready yet
                            }
                          }
                        }
                        setTimeout(checkChartReady, 100);
                      };

                      checkChartReady();
                    }}
                  ></Chart>
                  {(['bar', 'line'].includes(showChatsType?.value))
                    && isChartReady
                    && chartEventSetting.showEventBubbles
                    && chartRef.current && (
                      <EventWindows
                        key={Object.keys(chartEventSetting.bubbleEvent || {}).join(',')}
                        myChart={chartRef.current}
                        eventList={chartEventSetting.bubbleEvent}
                        format="YYYY-MM"
                        maxTimeValue={revenueData[revenueData.length - 1]?.month || ''}
                        showEventAxis={false}
                        dateType="monthly"
                        xAxisData={allData[showChatsType?.value]?.xAxisData || []}
                        yAxisMin={false}
                        topPadding={20}
                        eventBoxClassName="dbs-event-box--overall-financials"
                      />
                    )}
                </div>
              )}
              {showChatsType?.value === 'table' && allData[showChatsType?.value] && (
                <TableContent
                  className="dbs-commons-table dbs-chats-table"
                  // height={'360px'}
                  // ref={tableContent}
                  // searchForm={searchForm}
                  datas={allData[showChatsType?.value]?.data}
                  columns={allData[showChatsType?.value]?.columns}
                  sort={[{ by: 'fill_time', order: 'desc' }]}
                  resultKey="list"
                  recordKey="date"
                  // loading={tableLoading}
                  // preParams={preParams}
                  // lastResultData={lastResultData}
                  // scroll={{ x: 'max-content', y: 55 * 5 }}
                  // handerSearch={true}
                  // immediately={false}
                  addons={[
                    // 支持表格滚动，高度超过 192 开始显示滚动条
                    scrollable({
                      // maxHeight: '310px',
                      // minWidth: 2000,
                    }),
                  ]}
                ></TableContent>
              )}
            </>
          )}
        </CardCommon>

        <CardCommon
          title={rendTitle({
            title: `${formatMessage({ id: 'dbs_profit_trend' })} ($)`,
            tips: `${formatMessage({ id: 'dbs_update_on' })} ${profitUpdateTime}`,
          })}
          operation={
            <RenderOperations
              defaultValue={showOverallProfitChatsType.value}
              defaultValueShow={showOverallProfitChatsType.format}
              change={handleShowOverallProfitChatsTypeChange}
              options={pickOperations(['view', ...SHOW_REVENUE_TREND_LIST, 'download'], formatMessage)}
              downloadParams={{
                ...allOverallProfitData.table,
                name: formatMessage({ id: 'dbs_profit_trend' }),
              }}
              enableDownloadDropdown={true}
            ></RenderOperations>
          }
        >
          <Justify
            bottom
            left={
              <div className="dbs-overall-financials-revenue-date">
                {OverallNowProfitList(formatMessage).map((item: any) => {
                  const itemData = (OverallNowProfitData as any)?.[item.key];
                  const isYoyOrMom = item.key === 'yoy' || item.key === 'mom';

                  return (
                    <div className="dbs-overall-financials-item" key={item.key}>
                      <div className="dbs-overall-financials-item-header">
                        <div className="dbs-overall-financials-item-title">{item?.title}</div>
                      </div>
                      {isYoyOrMom ? (
                        // eslint-disable-next-line prettier/prettier
                        <YoyMomDisplay
                          date={itemData?.date}
                          value={itemData?.value}
                          type={item.key as 'yoy' | 'mom'}
                        />
                      ) : (
                        <>
                          <div className="dbs-revenue-date-desc">{itemData?.date}</div>
                          <DataProcessing
                            content={['$', item?.content]}
                            data={itemData?.data}
                            rate={itemData?.rate || ''}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            }
            right={''}
          />
          {profitTrendLoading ? (
            <Loading style={{ height: '343px' }} />
          ) : (
            <>
              {showOverallProfitChatsType?.value !== 'table' && allOverallProfitData[showOverallProfitChatsType?.value] && (
                <Chart
                  // key={`profit-${showOverallProfitChatsType?.value}-${showOverallProfitChatsType?.format}`}
                  data={allOverallProfitData[showOverallProfitChatsType?.value]}
                  type={showOverallProfitChatsType?.value}
                  height="343px"
                  setOtherConfigFunc={(opt: any) => {
                    opt = xAxisHighLight(opt, {
                      type: showOverallProfitChatsType?.value === 'bar' ? 'shadow' : 'line',
                      lineStyle: {
                        color: '#BBBDCB',
                        width: 1,
                        type: 'solid',
                      },
                    } as any);

                    // Add vertical line to separate 2024 and 2025
                    opt = addYearSeparator(opt);

                    // Hide tooltip when hovered month has no effective data.
                    if (opt.tooltip?.formatter) {
                      const originalFormatter = opt.tooltip.formatter;
                      opt.tooltip.formatter = withTooltipEmptyGuard((params: any[]) => originalFormatter(params));
                    }

                    // Configure data labels based on toggle state (format = true means show labels)
                    if (showOverallProfitChatsType?.format && opt.series && showOverallProfitChatsType?.value === 'bar') {
                      // Enable containLabel to ensure labels aren't cut off
                      if (opt.grid) {
                        opt.grid.containLabel = true;
                      }
                      opt.series = opt.series.map((series: any) => {
                        // Configure label for each data point based on value (positive = top, negative = bottom)
                        const newData = series.data?.map((value: any) => {
                          // Handle both array of numbers and array of objects
                          const numericValue = typeof value === 'number' ? value : value?.value ?? value;

                          if (numericValue === null || numericValue === undefined) {
                            return value; // Return original value if null/undefined
                          }

                          // Determine label position based on value sign
                          const labelPosition = numericValue >= 0 ? 'top' : 'bottom';

                          // Format the value using toMillions
                          const formattedValue = toMillions(numericValue);

                          // If value is an object, add label config; otherwise wrap it
                          if (typeof value === 'object' && value !== null) {
                            return {
                              ...value,
                              label: {
                                show: true,
                                position: labelPosition,
                                fontSize: 12,
                                fontWeight: 'normal',
                                color: '#333',
                                formatter: formattedValue,
                              },
                            };
                          } else {
                            return {
                              value: numericValue,
                              label: {
                                show: true,
                                position: labelPosition,
                                fontSize: 12,
                                fontWeight: 'normal',
                                color: '#333',
                                formatter: formattedValue,
                              },
                            };
                          }
                        });

                        return {
                          ...series,
                          data: newData,
                        };
                      });
                    }

                    return opt;
                  }}
                ></Chart>
              )}
              {showOverallProfitChatsType?.value === 'table' && allOverallProfitData[showOverallProfitChatsType?.value] && (
                <TableContent
                  className="dbs-commons-table dbs-chats-table"
                  datas={allOverallProfitData[showOverallProfitChatsType?.value]?.data}
                  columns={allOverallProfitData[showOverallProfitChatsType?.value]?.columns}
                  sort={[{ by: 'fill_time', order: 'desc' }]}
                  resultKey="list"
                  recordKey="date"
                  addons={[
                    // 支持表格滚动，高度超过 192 开始显示滚动条
                    scrollable({
                      // maxHeight: '310px',
                      // minWidth: 2000,
                    }),
                  ]}
                ></TableContent>
              )}
            </>
          )}
        </CardCommon>
        <CardCommon
          title={rendTitle({
            title: `${formatMessage({ id: 'dbs_iegg_overall_income_dashboard' })} (M$)`,
            tips: buildUpdateTime(incomePeriod),
          })}
          className={'dbs-iegg-financials-dashboard-card-common'}
          operation={
            <RenderOperations
              defaultValue={showOverallProfitChatsType.value}
              defaultValueShow={showOverallProfitChatsType.format}
              change={handleShowOverallProfitChatsTypeChange}
              options={pickOperations(['download'], formatMessage)}
              enableDownloadDropdown={true}
              customDownloadOptions={ieggDownloadOptions}
              customDownloadLoading={isDownloading}
            ></RenderOperations>
          }
        >
          <div style={{ marginTop: '20px' }}>
            <TableContent
              className="dbs-pub-table-points-content dbs-commons-table dbs-iegg-financials-dashboard"
              recordKey={(record: any) =>
                // 这里要注意，原始数据中数据类型全是字符串，不转成字符串会导致 key 值匹配不上
                String(record.id)
              }
              datas={tableData}
              loading={tableDataLoading}
              columns={IEGGFinancialsDashboardColumns(
                formatMessage,
                isAllExpanded,
                incomePeriod,
                stableToggleExpandAll,
                isNarrowWidth,
              )}
              stickyHeader={true}
              addons={[
                expandable({
                  // 已经展开的产品
                  expandedKeys,
                  // 产品展开为消息行
                  expand: (record) => record.children || null,
                  // 发生展开行为时，回调更新展开键值
                  onExpandedKeysChange: handleExpandedKeysChange,
                  // 只有产品行允许展开（expanded: true 的节点不显示展开箭头，始终展开）
                  shouldRecordExpandable: (record) => {
                    if (record?.id === 'all-total' || record?.Project === 'Total') return false;
                    if (record?.name === 'IEGG Total' || record?.project === 'IEGG Total') return false;
                    // expanded: true 的节点不显示展开箭头
                    if (record?.expanded === true) return false;
                    return Boolean(record?.children);
                  },
                  // shouldRecordExpandable: record => !isSBDEditing && Boolean(record.children),
                  targetColumnKey: 'name',
                  rowExpand: true,
                }),
                injectable({
                  row: (props, context) => ({
                    style: {
                      ...(props.style || {}),
                    },
                  }),
                  cell: (props, context: any) => {
                    const colIdx = context?.columnIndex;
                    const record = context?.record;
                    let bg = '';
                    let paddingLeft: string | undefined;

                    // 判断是否为第一级数据（IEGG Total 或 _depth <= 1 的数据）
                    const depth = record?._depth || 0;
                    const isFirstLevel = depth <= 1 && record;
                    const isSecondLevel = depth === 2 && record;

                    if (colIdx === 0) {
                      // name 列：只对表格体生效（有 record 数据），表头不设置背景色
                      if (record && isFirstLevel) {
                        bg = `rgba(228, 226, 247, 1)`;
                      }
                      if (record && isSecondLevel) {
                        bg = `rgba(245, 245, 248, 1)`;
                      }
                      // 层级缩进：让展开箭头和文字一起按层级缩进
                      const isIEGGTotal = record?.name === 'IEGG Total' || record?.project === 'IEGG Total';
                      // 判断是否显示展开箭头（与 shouldRecordExpandable 逻辑一致）
                      const hasExpandArrow = !(isIEGGTotal || record?.expanded === true) && Boolean(record?.children);
                      // 无箭头的行需要额外补偿箭头宽度（约20px），使 name 与有箭头的行对齐
                      const arrowOffset = hasExpandArrow ? 0 : 20;
                      if (depth > 0) {
                        // 如果是isThirdLevel，depth - 1再 *20
                        if (depth > 2) {
                          paddingLeft = `${(depth - 1) * 20 + arrowOffset}px`;
                        } else {
                          paddingLeft = `${depth * 20 + arrowOffset}px`;
                        }
                      }
                      if (isIEGGTotal) {
                        paddingLeft = `${1 * 22 + arrowOffset}px`;
                      }
                    } else if (colIdx === 1 || colIdx === 2) {
                      // actual_gross_revenue, actual_yoy_gross_revenue — #7F6FF1 6%
                      if (isFirstLevel) {
                        bg = `rgba(228, 226, 247, 1)`;
                      } else if (isSecondLevel) {
                        bg = `rgba(239,237,247,1)`;
                      } else {
                        bg = `rgba(127, 111, 241, ${0.06})`;
                      }
                    } else if (colIdx === 3 || colIdx === 4) {
                      // budget_gross_revenue, actual_vs_budget_gross_revenue — #7F6FF1 8%
                      // bg = `rgba(127, 111, 241, ${0.08 + alphaBoost})`;
                      if (isFirstLevel) {
                        bg = `rgba(220, 217, 247, 1)`;
                      } else if (isSecondLevel) {
                        bg = `rgba(228, 226, 247, 1)`;
                      } else {
                        bg = `rgba(127, 111, 241, ${0.08})`;
                      }
                    } else if (colIdx === 5 || colIdx === 6) {
                      // forecast_gross_revenue, forecast_vs_budget_gross_revenue — #7F6FF1 16%
                      // bg = `rgba(127, 111, 241, ${0.16 + alphaBoost})`;
                      if (isFirstLevel) {
                        bg = `rgba(210, 205, 246, 1)`;
                      } else if (isSecondLevel) {
                        bg = `rgba(220, 217, 247, 1)`;
                      } else {
                        bg = `rgba(127, 111, 241, ${0.16})`;
                      }
                    } else if (colIdx === 7) {
                      // actual_profit — #5EC8F1 6%
                      // bg = `rgba(94, 200, 241, ${0.06 + alphaBoost})`;
                      if (isFirstLevel) {
                        bg = `rgba(222, 233, 247, 1)`;
                      } else if (isSecondLevel) {
                        bg = `rgba(236, 242, 248, 1)`;
                      } else {
                        bg = `rgba(94, 200, 241, 0.06)`;
                      }
                    } else if (colIdx >= 8 && colIdx <= 10) {
                      // forecast_profit, forecast_vs_budget_profit — #5EC8F1 16%
                      // bg = `rgba(94, 200, 241, ${0.16 + alphaBoost})`;
                      if (isFirstLevel) {
                        bg = `rgba(205, 226, 246, 1)`;
                      } else if (isSecondLevel) {
                        bg = `rgba(222, 233, 247, 1)`;
                      } else {
                        bg = `rgba(222, 233, 247, 1)`;
                      }
                    }
                    return {
                      style: {
                        ...(props.style || {}),
                        background: bg,
                        paddingLeft,
                      },
                    };
                  },
                }),
                groupable({
                  headerGroups: [
                    {
                      key: 'gross_revenue',
                      title: (
                        <div
                          className="dbs-overall-financials-group-header dbs-overall-financials-group-header--gross-revenue"
                          style={{
                            textAlign: 'center',
                            background: 'rgba(127, 111, 241, 0.06)',
                            margin: '-8px -16px',
                            padding: '8px 16px',
                          }}
                        >
                          Gross Revenue
                        </div>
                      ),
                      align: 'center',
                      children: [
                        'actual_gross_revenue',
                        'actual_yoy_gross_revenue',
                        'budget_gross_revenue',
                        'actual_vs_budget_gross_revenue',
                        'forecast_gross_revenue',
                        'forecast_vs_budget_gross_revenue',
                      ],
                    },
                    {
                      key: 'profit',
                      title: (
                        <div
                          className="dbs-overall-financials-group-header dbs-overall-financials-group-header--profit"
                          style={{
                            textAlign: 'center',
                            background: 'rgba(94, 200, 241, 0.06)',
                            margin: '-8px -16px',
                            padding: '8px 16px',
                          }}
                        >
                          Profit
                        </div>
                      ),
                      align: 'center',
                      children: ['actual_profit', 'budget_profit', 'forecast_profit', 'forecast_vs_budget_profit'],
                    },
                  ],
                }),
              ]}
            ></TableContent>
          </div>
        </CardCommon>
        {/* <CardCommon title={rendTitle({ title: `${formatMessage({ id: 'dbs_iegg_overall_income_dashboard' })} ($)`, tips: buildUpdateTime(incomePeriod) })} >
          <div style={{ marginTop: '20px' }}>
            <TableContent
              className="dbs-revenue-projects-table dbs-pub-table-points-content dbs-commons-table dbs-chats-table"
              // height={'360px'}  className='dbs-table-phase'
              recordKey={(record: any) =>
                // 这里要注意，原始数据中数据类型全是字符串，不转成字符串会导致 key 值匹配不上
                String(record.id)
              }
              datas={tableData}
              columns={OverallIncomeColums(formatMessage, incomePeriod, toggleExpandAll, isAllExpanded)}
              // lastResultData={(data: any)=> {setRecordsPhase(data);return data}}
              addons={[
                // 支持表格滚动，高度超过 192 开始显示滚动条
                // scrollable({
                //   maxHeight: '310px',
                //   // minWidth: 2000,
                // }),
                expandable({
                  // 已经展开的产品
                  expandedKeys,
                  // 产品展开为消息行
                  expand: record => record.children || null,
                  // 发生展开行为时，回调更新展开键值
                  onExpandedKeysChange: keys => setExpandedKeys(keys),
                  // 只有产品行允许展开
                  shouldRecordExpandable: record => Boolean(record.children),
                  targetColumnKey: "category",
                  rowExpand: true
                }),
                injectable({
                  row: (props, context) => ({
                    style: {
                      ...(props.style || {}),
                      background: context.record?.children ? "#F5F5F8" : undefined,
                    },
                  }),
                  cell: (props, context: any) => {
                    return {
                      style: {
                        ...(props.style || {}),
                        background: context?.columnIndex > 1 && context?.columnIndex < 7 ? "rgba(127, 111, 241, 0.06)" : context?.columnIndex > 6 ? 'rgba(94, 200, 241, 0.06)' : '',
                      }
                    }
                  }
                }),
              ]}
            ></TableContent>
          </div>
        </CardCommon> */}

        <EventSetting
          visible={eventSettingVisible}
          close={() => setEventSettingVisible(false)}
          confirm={(params: any) => {
            setChartEventSetting((prev: any) => ({
              ...prev,
              bubbleEvent: params.bubbleEvent,
            }));
            setEventSettingVisible(false);
          }}
          data={eventDataByGame}
          timeFormat="YYYY-MM"
          dateType="monthly"
          xAxisData={allData[showChatsType?.value]?.xAxisData?.filter((x: string) => x !== '') || []}
        />
      </div>
    </>
  );
}
